/**
 * Third-person follow camera.
 *
 * Two details matter more than they look:
 *   - LOOK-AHEAD. The camera leads the cat's velocity slightly, so you see where
 *     you are going rather than where you have been. Without it, fast movement
 *     feels like you are chasing the camera.
 *   - WALL PULL-IN. A raycast from the cat to the desired camera position pulls
 *     the camera in when a wall is behind you. Interiors are cramped and without
 *     this you spend half the game looking at the inside of a couch.
 *
 * The FOV widens during Frenzy - a wordless way to signal panic that a
 * 6-year-old reads instantly without any UI at all.
 */

import * as THREE from 'three';
import type { Vec3 } from '@/core/math';
import { clamp, damp, DEG_TO_RAD } from '@/core/math';
import { tuning } from '@/core/tuning';
import type { PhysicsWorld } from './physicsWorld';

const MIN_PITCH = -70 * DEG_TO_RAD;
const MAX_PITCH = 35 * DEG_TO_RAD;

export class FollowCamera {
  /** Horizontal orbit angle, radians. */
  yaw = 0;
  /** Vertical orbit angle, radians. Negative looks down at the cat. */
  pitch = tuning.camera.pitchDeg * DEG_TO_RAD;

  private currentTarget = new THREE.Vector3();
  private currentPosition = new THREE.Vector3();
  private shake = 0;
  private fovBoost = 0;
  private initialized = false;

  private readonly scratchDesired = new THREE.Vector3();
  private readonly scratchLookAt = new THREE.Vector3();

  constructor(
    readonly camera: THREE.PerspectiveCamera,
    private readonly physics: PhysicsWorld,
  ) {}

  /** Applies accumulated mouse/stick look delta. Called once per fixed step. */
  applyLook(deltaX: number, deltaY: number): void {
    this.yaw -= deltaX;
    this.pitch = clamp(this.pitch - deltaY, MIN_PITCH, MAX_PITCH);
  }

  /** Camera-relative forward/right, so WASD moves the cat relative to the view. */
  getMovementBasis(): { forward: Vec3; right: Vec3 } {
    const sin = Math.sin(this.yaw);
    const cos = Math.cos(this.yaw);
    // Three's convention is -Z forward.
    return {
      forward: { x: -sin, y: 0, z: -cos },
      right: { x: cos, y: 0, z: -sin },
    };
  }

  addShake(amount: number): void {
    this.shake = Math.min(1, this.shake + amount);
  }

  /** 0 for calm, 1 during Frenzy. Interpolated internally. */
  setFrenzy(intensity: number): void {
    this.fovBoost = clamp(intensity, 0, 1);
  }

  /**
   * Presentation-only, so it takes the REAL frame delta and runs after the sim.
   * `focus` is the cat's head position; `velocity` its current world velocity.
   */
  update(focus: Vec3, velocity: Vec3, realDt: number): void {
    const cfg = tuning.camera;

    const lookAhead = cfg.lookAheadFactor;
    this.scratchLookAt.set(
      focus.x + velocity.x * lookAhead,
      focus.y + cfg.height * 0.35,
      focus.z + velocity.z * lookAhead,
    );

    if (!this.initialized) {
      this.currentTarget.copy(this.scratchLookAt);
      this.initialized = true;
    } else {
      // Exponential smoothing on the LOOK TARGET rather than the camera
      // position. Smoothing the position instead makes the camera feel like it
      // is on a rubber band, which reads as sluggish.
      this.currentTarget.x = damp(
        this.currentTarget.x,
        this.scratchLookAt.x,
        cfg.followLag,
        realDt,
      );
      this.currentTarget.y = damp(
        this.currentTarget.y,
        this.scratchLookAt.y,
        cfg.followLag,
        realDt,
      );
      this.currentTarget.z = damp(
        this.currentTarget.z,
        this.scratchLookAt.z,
        cfg.followLag,
        realDt,
      );
    }

    const cosPitch = Math.cos(this.pitch);
    const offsetX = Math.sin(this.yaw) * cosPitch;
    const offsetY = -Math.sin(this.pitch);
    const offsetZ = Math.cos(this.yaw) * cosPitch;

    let distance = cfg.distance;

    // Pull in when geometry sits between the cat and the desired position.
    const eye = { x: focus.x, y: focus.y + cfg.height * 0.5, z: focus.z };
    this.scratchDesired.set(
      eye.x + offsetX * distance,
      eye.y + offsetY * distance + cfg.height * 0.4,
      eye.z + offsetZ * distance,
    );
    const blocked = this.firstHitDistance(eye, this.scratchDesired);
    if (blocked !== undefined) distance = Math.max(0.9, blocked - cfg.collisionPadding);

    // Scale the vertical lift with how far the camera actually ended up.
    //
    // Applying the full lift at a pulled-in distance is what turned the opening
    // shot into a near-overhead view: with the bed against a wall the camera got
    // clamped to its minimum distance but kept its full height, so it climbed
    // directly above the cat. Shrinking the lift with the distance keeps it
    // BEHIND the cat in tight spaces, which is what a player needs to steer.
    const lift = cfg.height * 0.4 * (distance / cfg.distance);

    this.currentPosition.set(
      eye.x + offsetX * distance,
      eye.y + offsetY * distance + lift,
      eye.z + offsetZ * distance,
    );

    // Impact shake. Decays fast - a lingering shake reads as a bug.
    if (this.shake > 0.001) {
      const magnitude = this.shake * cfg.shakeOnImpact;
      this.currentPosition.x += (Math.random() - 0.5) * magnitude;
      this.currentPosition.y += (Math.random() - 0.5) * magnitude;
      this.currentPosition.z += (Math.random() - 0.5) * magnitude;
      this.shake = damp(this.shake, 0, 0.0001, realDt);
    }

    this.camera.position.copy(this.currentPosition);
    this.camera.lookAt(this.currentTarget);

    const targetFov = cfg.fovDeg + this.fovBoost * cfg.frenzyFovBoost;
    if (Math.abs(this.camera.fov - targetFov) > 0.05) {
      this.camera.fov = damp(this.camera.fov, targetFov, 0.05, realDt);
      this.camera.updateProjectionMatrix();
    }
  }

  /** Distance to the first sight-blocking hit, or undefined if the path is clear. */
  private firstHitDistance(from: Vec3, to: THREE.Vector3): number | undefined {
    const target = { x: to.x, y: to.y, z: to.z };
    if (this.physics.hasLineOfSight(from, target)) return undefined;
    // Binary search the free distance. Four iterations is visually smooth enough
    // and far cheaper than a shape cast every frame.
    let low = 0.3;
    let high = Math.hypot(target.x - from.x, target.y - from.y, target.z - from.z);
    const direction = {
      x: (target.x - from.x) / high,
      y: (target.y - from.y) / high,
      z: (target.z - from.z) / high,
    };
    for (let i = 0; i < 4; i++) {
      const mid = (low + high) / 2;
      const point = {
        x: from.x + direction.x * mid,
        y: from.y + direction.y * mid,
        z: from.z + direction.z * mid,
      };
      if (this.physics.hasLineOfSight(from, point)) low = mid;
      else high = mid;
    }
    return low;
  }

  /**
   * Orients the camera so it looks from `focus` toward `target`, placing itself
   * on the opposite side.
   *
   * Needed because the cat bed sits in a corner: with a default yaw of 0 the
   * camera starts buried in the nearest wall, gets pulled to its minimum
   * distance, and the player's first view of the game is the flat side of a
   * bookshelf. Aiming at the room centre instead opens on the whole room.
   */
  faceTowards(focus: Vec3, target: Vec3): void {
    const dx = focus.x - target.x;
    const dz = focus.z - target.z;
    if (Math.hypot(dx, dz) < 0.01) return;
    // Camera offset direction is (sin(yaw), cos(yaw)); we want it pointing from
    // the target back through the cat, so the view looks inward.
    this.yaw = Math.atan2(dx, dz);
  }

  /** Snap instantly, with no smoothing. Used on level load and after a catch. */
  reset(focus: Vec3): void {
    this.initialized = false;
    this.shake = 0;
    this.update(focus, { x: 0, y: 0, z: 0 }, 1 / 60);
  }
}
